import CollapsedPanel from '../components/CollapsedPanel';
import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';
import styled from 'styled-components';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { EditorContext } from '../../../utils/contexts';
import { Tooltip, TextField } from '@mui/material';
import Switcher from '../components/Switcher';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import { Label } from '../Steps/components';
import InputBlock from '../components/InputBlock';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DOMPurify from 'dompurify';
import EditIcon from '@mui/icons-material/Edit';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import useAPI from '../../../utils/api';
import SalesforceReconnectAlert from '../../../shared/SalesforceReconnectAlert';

const FIELD_TYPE = [
  { label: 'Email', value: 'email' },
  { label: 'Text', value: 'text' },
  { label: 'URL', value: 'url' },
  { label: 'Phone', value: 'tel' },
  { label: 'Dropdown', value: 'select' },
  { label: 'Checkbox', value: 'checkbox' },
  { label: 'Multi-select', value: 'multi_select' },
];

const Hint = styled.div`
  font-size: 0.9em;
  font-weight: normal;
  color: #9e9e9e;
`;

const CheckButton = styled.div`
  display: inline;

  svg {
    color: ${(props) => (props.enabled ? '#27ab9e' : '#c6c6c6')};
    line-height: 80%;
    font-size: 1.5em;
    text-align: center;
    font-style: normal;
    font-weight: lighter;
    margin-right: 0.5rem;
    flex: 0 0 2rem;
  }
`;

const CloseButton = styled.div`
  display: inline;

  svg {
    color: #c6c6c6;
    line-height: 80%;
    font-size: 1.5em;
    text-align: center;
    font-style: normal;
    font-weight: lighter;
    margin-right: 0.5rem;
    flex: 0 0 2rem;
  }
`;

const Buttons = styled.div`
  right: 1rem;
  position: absolute;
  font-size: 1.25rem;
  font-weight: lighter;
  transition: all 0.15s ease-out 0s;
  top: 50%;
  transform: translateY(-50%);
`;

const StyledQuill = styled(ReactQuill)`
  .ql-container.ql-snow {
    height: 60px;
  }

  .ql-editor {
    overflow-y: auto;
  }

  /* Custom Color Picker Icon */
  .ql-custom-color {
    background: url('data:image/svg+xml;utf8,<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8zm-5.5 9c-.83 0-1.5-.67-1.5-1.5S5.67 9 6.5 9s1.5.67 1.5 1.5S7.33 12 6.5 12zm3-4C8.67 8 8 7.33 8 6.5S8.67 5 9.5 5s1.5.67 1.5 1.5S10.33 8 9.5 8zm5 0c-.83 0-1.5-.67-1.5-1.5S13.67 5 14.5 5s1.5.67 1.5 1.5S15.33 8 14.5 8zm3 4c-.83 0-1.5-.67-1.5-1.5S16.67 9 17.5 9s1.5.67 1.5 1.5S18.33 12 17.5 12z" fill="%23666"/></svg>') no-repeat center center !important;
    background-size: 18px 18px !important;
    width: 28px !important;
  }

  /* Fix Color Palette Clipping caused by Sidebar Edge */
  .ql-snow .ql-color-picker .ql-picker-options {
    /* Anchor to the right, expand to the left */
    right: -10px !important;
    left: auto !important; 
    
    /* Strict sizing so it wraps cleanly */
    width: 152px !important; 
    max-height: 200px !important;
    overflow-y: auto !important;
    padding: 6px !important;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2) !important;
    
    z-index: 99999 !important;
    background: #fff !important;
  }

  .ql-snow .ql-color-picker .ql-picker-item {
    margin: 2px !important;
    float: left !important;
    height: 16px !important;
    width: 16px !important;
  }
`;

const QUILL_COLORS = [
  "#000000", "#434343", "#666666", "#999999", "#CCCCCC", "#FFFFFF",
  "#FF0000", "#FF9900", "#FFFF00", "#00FF00", "#0000FF", "#9900FF",
  "#E67E22", "#F39C12", "#F1C40F", "#2ECC71", "#27AE60", "#16A085",
  "#1ABC9C", "#3498DB", "#2980B9", "#9B59B6", "#8E44AD", "#34495E",
  "#EA9999", "#F9CB9C", "#FFE599", "#B6D7A8", "#A2C4C9", "#A4C2F4",
  "#B4A7D6", "#D5A6BD", "#E06666", "#F6B26B", "#FFD966", "#93C47D",
  "#76A5AF", "#6D9EEB", "#8E7CC3", "#C27BA0", "#CC0000", "#E69138",
  "#F1C232", "#6AA84F", "#45818E", "#3C78D8", "#674EA7", "#A64D79",
  "#990000", "#B45F06", "#BF9000", "#38761D", "#134F5C", "#1155CC",
  "#351C75", "#741B47", "#660000", "#783F04", "#7F6000", "#274E13",
  "#0C343D", "#1C4587", "#20124D", "#4C1130"
];

export const FieldForm = styled(
  ({ className, builtinField, fieldData, updateField, salesforceFields }) => {
    const [isHtmlEditMode, setIsHtmlEditMode] = useState(false);
    const quillRef = useRef(null);
    const colorInputRef = useRef(null);

    const applyColor = (color) => {
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        quill.format('color', color);
      }
    };

    const quillModules = useMemo(() => ({
      toolbar: {
        container: [
          [{ header: [1, 2, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: QUILL_COLORS }, { background: [] }],
          ['link', 'custom-color'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['clean']
        ],
        handlers: {
          'custom-color': () => {
            if (colorInputRef.current) colorInputRef.current.click();
          }
        }
      }
    }), []);

    return (
      <div className={className}>
        {!builtinField && salesforceFields && salesforceFields.length > 0 ? (
          <TextField
            fullWidth
            size="small"
            margin="dense"
            select
            value={fieldData.name}
            onChange={(e) => updateField('name', e.target.value)}
            variant="outlined"
            label="Salesforce Field"
            required
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: { maxHeight: 300, width: 'auto', maxWidth: '100%' }
                }
              }
            }}
          >
            {salesforceFields.map((f) => (
              <MenuItem key={f.id} value={f.id} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {f.name}
              </MenuItem>
            ))}
          </TextField>
        ) : !builtinField && (
          <TextField
            size="small"
            margin="dense"
            value={fieldData.name}
            onChange={(e) =>
              updateField(
                'name',
                e.target.value
                  .replace(/[^a-zA-Z0-9_ ]/g, '')
                  .replace(/\s/g, '_')
                  .toLowerCase()
                  .trim()
              )
            }
            variant="outlined"
            label="Field Name (for back-end)"
            placeholder="Name"
            required
          />
        )}
        {builtinField && (
          <TextField
            size="small"
            margin="dense"
            value={fieldData.id.replace('builtin-', '')}
            variant="outlined"
            label="Field Name (for back-end)"
            disabled
          />
        )}

        {!isHtmlEditMode ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TextField
              size="small"
              margin="dense"
              value={fieldData.label}
              onChange={(e) => updateField('label', e.target.value)}
              variant="outlined"
              label="Field Label (for front-end)"
              placeholder="Label"
              required
              style={{ flexGrow: 1 }}
            />
            {fieldData.type === 'checkbox' &&(
            <Tooltip title="Edit with HTML Editor">
              <EditIcon
                style={{ cursor: 'pointer' }}
                onClick={() => setIsHtmlEditMode(true)}
              />
            </Tooltip>
            )}
          </div>
        ) : (
          <div style={{ marginTop: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Field Label (HTML Editor)
            </label>
            <div style={{ position: 'relative' }}>
              <StyledQuill
                theme="snow"
                ref={quillRef}
                value={fieldData.label}
                onChange={(content) => updateField('label', content)}
                modules={quillModules}
              />
              <input
                type="color"
                ref={colorInputRef}
                style={{ position: 'absolute', visibility: 'hidden' }}
                onChange={(e) => applyColor(e.target.value)}
              />
            </div>
            <button
              onClick={() => setIsHtmlEditMode(false)}
              style={{
                marginTop: '1rem',
                padding: '0.4rem 1.5rem',
                background: '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Done
            </button>
          </div>
        )}

        {!builtinField && (
          <InputBlock label="Field Type">
            <TextField
              size="small"
              variant="outlined"
              select
              value={fieldData.type}
              onChange={(e) => {
                updateField('type', e.target.value);
                if (e.target.value === 'multi_select') {
                  updateField('options', []);
                }
              }}
            >
              {FIELD_TYPE.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </InputBlock>
        )}
        {(fieldData.type === 'select' || fieldData.type === 'multi_select') && (
          <TextField
            value={fieldData.options}
            multiline
            onChange={(e) => {
              const options = e?.target?.value?.split(',');
              if (
                fieldData.type === 'multi_select' &&
                (!Array.isArray(options) ||
                  (Array.isArray(options) && options.length > 15))
              )
                return;
              updateField('options', e.target.value);
            }}
            label="Options"
            placeholder="Option 1, Option 2, ..."
            size="small"
            required
          />
        )}
        {(fieldData.type === 'multi_select') && (
          <InputBlock label="Option Columns">
            <TextField
              size="small"
              variant="outlined"
              select
              value={fieldData.option_columns || 3}
              onChange={(e) => {
                updateField('option_columns', e.target.value);
              }}
            >
              {[1, 2, 3, 4, 5].map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
          </InputBlock>
        )}
        <Label>
          <span>Required</span>
          <Switcher
            checked={fieldData.required}
            onChange={() => updateField('required', !fieldData.required)}
          />
        </Label>
      </div>
    );
  }
)`
  padding-right: 0.67em;
  background-color: white;
  max-height: ${(props) => (props.active ? 'auto' : '0')};
  transition: max-height 0.2s ease-out;

  & .input-wrapper {
    width: auto;
  }
`;

const FieldContainer = styled.div`
  margin-top: 0.375rem;
  padding-left: 0.67em;
  border: 1px solid rgba(145, 145, 145, 0.5);
  color: #666666;
  border-radius: 5px;

  ${CloseButton} {
    display: none;
  }

  &:hover ${CloseButton} {
    display: inline;
  }
`;

const Field = styled.div`
  font-weight: normal;
  opacity: 0.75;
  color: #666666;
  line-height: 120%;
  position: relative;
  min-height: 2.75rem;
  justify-content: start;
  align-items: center;
  display: flex;
  transition: all 0.15s ease-out 0s;
  cursor: pointer;

  .drag_icon {
    margin-right: 5px;
    cursor: move;
    cursor: grab;
  }

  & .field-label {
    min-width: 200px;
    min-height: 20px;
    cursor: move;
    cursor: grab;

    * {
      color: inherit;
    }

    &:hover {
      color: #27ab9e;
    }
  }

  span {
    width: 100%;
  }
`;

const AddField = styled(Field)`
  color: ${(props) => (props.$disabled ? '#c3c3c3' : '#27ab9e')};
  border: 1px solid rgba(145, 145, 145, 0.5);
  padding: 0.5em 4.9em 0.5em 0.67em;
  border-radius: 5px;
  margin-top: 0.375rem;
  text-decoration: underline;
  justify-content: start;

  span {
    display: inline-flex;
  }

  svg {
    margin-left: 0.1em;
    width: 0.7em;
    height: 0.7em;
  }
`;

function InputField({ field, onChange, onDelete, salesforceFields }) {
  const updateField = (key, value) => {
    field[key] = value;
    onChange(field);
  };

  const toggleFieldEnabled = (field) => {
    field.is_enabled = !field.is_enabled;
    onChange(field);
  };

  return (
    <FieldContainer>
      <Field key={field.id} $editing={true}>
        <DragIndicatorIcon className="drag_icon" />
        <div className="field-label" dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(field.label, {
            ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'div', 'span', 'ul', 'li'],
            ALLOWED_ATTR: ['href', 'target', 'rel']
          })
        }} >
        </div>
        <Buttons>
          <CloseButton onClick={() => onDelete(field)}>
            <CloseIcon />
          </CloseButton>
          <CheckButton
            enabled={field.is_enabled}
            onClick={() => toggleFieldEnabled(field)}
          >
            <CheckCircleIcon />
          </CheckButton>
        </Buttons>
      </Field>
      <FieldForm active={true} fieldData={field} updateField={updateField} salesforceFields={salesforceFields} />
    </FieldContainer>
  );
}

function BuiltinInputField({ field, onChange }) {
  const { settings } = useContext(EditorContext);

  const updateField = (key, value) => {
    field[key] = value;
    onChange(field);
  };

  const toggleFieldEnabled = (field) => {
    field.is_enabled = !field.is_enabled;
    onChange(field);
  };

  const isEmail =
    (settings.subtype === 'email' || settings.subtype === 'gamification') &&
    field.id === 'builtin-email';
  const tooltip = isEmail
    ? "It can't be disabled as it is the goal itself to collect emails"
    : '';

  return (
    <FieldContainer>
      <Field key={field.id} $editing={false}>
        <DragIndicatorIcon className="drag_icon" />
        <div className="field-label" dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(field.label, {
            ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'div', 'span', 'ul', 'li'],
            ALLOWED_ATTR: ['href', 'target', 'rel']
          })
        }}></div>
        <Buttons>
          <CheckButton
            enabled={field.is_enabled}
            onClick={() => (isEmail ? null : toggleFieldEnabled(field))}
          >
            <Tooltip title={tooltip} placement="right-start">
              <CheckCircleIcon />
            </Tooltip>
          </CheckButton>
        </Buttons>
      </Field>
      <FieldForm
        builtinField={true}
        active={true}
        fieldData={field}
        updateField={updateField}
      />
    </FieldContainer>
  );
}

export default function TextFieldsPanel() {
  const { settings, updateSettings } = useContext(EditorContext);
  const api = useAPI();
  const [salesforceFields, setSalesforceFields] = useState([]);
  const [salesforceDisconnected, setSalesforceDisconnected] = useState(false);

  const contactList = settings.site?.contact_lists?.find(
    (cl) => cl.id === settings.contact_list_id
  );
  const isSalesforce = contactList?.provider === 'salesforce';
  const recordType = contactList?.data?.record_type || 'Contact';

  const fetchSalesforceFields = () => {
    api.connectionFields('salesforce', recordType).then(([ok, fields]) => {
      if (ok) {
        setSalesforceDisconnected(false);
        setSalesforceFields(fields || []);
      } else if (fields?.error === 'session_expired') {
        setSalesforceDisconnected(true);
        setSalesforceFields([]);
      }
    });
  };

  useEffect(() => {
    if (isSalesforce) {
      fetchSalesforceFields();
    } else {
      setSalesforceDisconnected(false);
      setSalesforceFields([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.contact_list_id]);

  const customFields = settings.settings.fields_to_collect.reduce(
    (memo, field) => {
      return { ...memo, [field.id]: field };
    },
    {}
  );

  const [fields, setFields] = useState(customFields);

  const updateCustomSettings = (props) => {
    const customSettings = { ...settings.settings, ...props };
    updateSettings({ settings: customSettings });
  };

  const customFieldsDisabled = settings.type === 'Bar';

  const addField = () => {
    const lastField = Object.values(fields).slice(-1)[0];
    if (
      Object.values(fields).length > 3 &&
      (lastField.label === '' || lastField.name === '')
    ) {
      onFieldDelete(lastField);
    } else {
      if (!customFieldsDisabled) {
        const newField = {
          id: `field_${Date.now()}`,
          type: 'text',
          label: '',
          name: '',
          required: false,
          is_enabled: true,
          editable: true,
          editing: true,
        };
        onFieldChange(newField);
      }
    }
  };

  useEffect(() => {
    if (Object.keys(fields).length > 0) {
      const fields_to_collect = Object.values(fields);
      updateCustomSettings({ fields_to_collect });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  useEffect(() => {
    const listener = (event) => {
      if (event.key === 'Escape' || event.key === 'Enter') {
        const field = Object.values(fields).find((f) => f.editing);

        if (field) {
          field.editing = false;
          setFields({ ...fields, [field.id]: field });
        }
      }
    };
    document.addEventListener('keydown', listener, false);

    return () => {
      document.removeEventListener('keydown', listener, false);
    };
  }, [fields]);

  const onFieldChange = (field) => {
    setFields({ ...fields, [field.id]: field });
  };

  const onFieldDelete = (field) => {
    delete fields[field.id];
    setFields({ ...fields });
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, newIndex) => {
    e.preventDefault();
    const oldIndex = e.dataTransfer.getData('text/plain');
    const newList = [...Object.values(fields)];
    const [removed] = newList.splice(oldIndex, 1);
    newList.splice(newIndex, 0, removed);
    const reHash = {};
    newList.forEach((field) => (reHash[field.id] = field));
    setFields(reHash);
  };

  return (
    <CollapsedPanel open={false} title="Input Fields">
      <Hint>Select which text fields you want to include in your popup.</Hint>

      {salesforceDisconnected && (
        <SalesforceReconnectAlert onReconnected={fetchSalesforceFields} />
      )}

      <ul>
        {Object.values(fields).map((field, index) => (
          <li
            key={index}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
          >
            {field.id.includes('builtin-') ? (
              <BuiltinInputField
                key={`field-${index}`}
                field={field}
                onChange={onFieldChange}
              />
            ) : (
              <InputField
                onChange={onFieldChange}
                onDelete={onFieldDelete}
                field={field}
                key={field.id}
                salesforceFields={salesforceFields}
              />
            )}
          </li>
        ))}
      </ul>
      <div>
        <AddField $disabled={customFieldsDisabled} onClick={addField}>
          <Tooltip
            title="Using any special character in the field name may hamper data collection."
            placement="right-start"
          >
            <span>
              Add Field <InfoIcon />
            </span>
          </Tooltip>
        </AddField>
      </div>
      {customFieldsDisabled && (
        <div className="hint">
          In order to add custom fields please select Modal, Slider or Takeover
          type on Type tab.
        </div>
      )}
    </CollapsedPanel>
  );
}
